import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';

/**
 * Selecting rows, and acting on the selection.
 *
 * Built once and shared, because the alternative is three list screens that each
 * invent their own checkbox behaviour and their own idea of what "select all"
 * means when a filter is applied.
 *
 * Selection is by id and survives re-sorting; it is cleared when the underlying
 * set changes enough that the coach can no longer see what they picked.
 */
export function useSelection(available: readonly string[]) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Ids that have since disappeared — archived, filtered out, deleted — must not
  // linger in a count the coach cannot see.
  const live = useMemo(() => {
    const present = new Set(available);
    const next = new Set<string>();
    for (const id of selected) if (present.has(id)) next.add(id);
    return next;
  }, [selected, available]);

  const toggle = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const set = useCallback((ids: readonly string[]) => setSelected(new Set(ids)), []);
  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected: live,
    count: live.size,
    has: (id: string) => live.has(id),
    toggle,
    all: () => set(available),
    clear,
    isAll: available.length > 0 && live.size === available.length,
  };
}

/** The checkbox on a row. Stops the click from opening the row behind it. */
export function RowCheck({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    // The box stays 16px — a row of them should read as a quiet column, not a
    // row of buttons — but the thing a thumb lands on is 44, and the negative
    // margin gives the space back so the row does not move. It has to be a
    // `label`: padding a `span` only swallowed the tap, because
    // `stopPropagation` keeps the row from opening without forwarding
    // anything to the box. A label forwards it natively.
    <label
      className="-mx-3.5 grid size-11 shrink-0 cursor-pointer place-items-center"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={label}
        className="size-4 accent-ink"
      />
    </label>
  );
}

/**
 * The bar that appears once something is selected.
 *
 * Fixed to the bottom rather than the top: on a phone it sits under the thumb,
 * and it never covers the rows being chosen.
 */
export function BulkBar({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  children: ReactNode;
}) {
  const t = useTranslations();
  if (count === 0) return null;

  return (
    <div
      role="region"
      aria-label={t('bulk.title')}
      className="sticky bottom-3 z-30 mt-4"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-(--radius-panel) border border-line-strong bg-surface px-3 py-2.5 shadow-(--shadow-raised)">
        <span className="font-condensed text-subhead font-semibold">
          {t('bulk.selected', { count })}
        </span>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
        <button
          type="button"
          onClick={onClear}
          className="ms-auto text-meta text-ink-faint underline-offset-2 hover:text-ink hover:underline"
        >
          {t('bulk.clear')}
        </button>
      </div>
    </div>
  );
}
